import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { ArrowRight, ShieldCheck, Zap, Globe2, Factory } from "lucide-react";
import { LANDING } from "../constants/testIds";

export default function Landing() {
  return (
    <div className="min-h-screen wizard-surface text-neutral-900">
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[4.5rem] flex items-center justify-between">
          <BrandLogo to="/" variant="default" />
          <nav className="flex items-center gap-6">
            <Link to="/rfq/new" data-testid={LANDING.navStart}>
              <Button className="btn-wizard-primary h-10 px-5 group">
                Start RFQ{" "}
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      <section
        data-testid={LANDING.hero}
        className="relative overflow-hidden text-white min-h-[520px] lg:min-h-[600px] flex items-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ backgroundImage: "url(/hero-demaxtore-bg.jpg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--brand-navy-dark))]/95 via-[hsl(var(--brand-navy))]/80 to-[hsl(var(--brand-navy))]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-navy-dark))]/60 via-transparent to-[hsl(var(--brand-navy-dark))]/30" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[hsl(var(--brand-red))] to-transparent opacity-90" />

        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[10px] uppercase tracking-[0.22em] text-white/85 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--brand-red))]" />
              Connecting Buyers with Turkish Suppliers
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              Request a quote in
              <br />
              <span className="text-[hsl(var(--brand-red-light))] font-normal">under 30 seconds.</span>
            </h1>
            <p className="mt-8 text-lg text-white/75 max-w-xl leading-relaxed">
              Tell us what you need. We match your request with verified Turkish manufacturers —
              no accounts, no forms, no friction.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/rfq/new" data-testid={LANDING.startRfqBtn}>
                <Button size="lg" className="btn-cta h-14 px-8 text-base group">
                  Start Your RFQ{" "}
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <span className="text-white/55 text-sm">No signup · No credit card</span>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="rounded-2xl bg-white/95 backdrop-blur-sm p-8 lg:p-10 shadow-2xl shadow-black/30 max-w-md w-full transition-all duration-500 hover:shadow-glow-red hover:-translate-y-1 border border-white/20 text-center">
              <BrandLogo to={null} variant="hero" centered />
              <p className="mt-4 text-center text-sm italic text-[hsl(var(--brand-navy))]/70">
                Connecting Buyers with Turkish Suppliers
              </p>
              <div className="mt-8 flex justify-center gap-3">
                <span className="accent-line" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--brand-navy))]/50 font-semibold">
                  How it works
                </span>
                <span className="accent-line" />
              </div>
              <ol className="mt-6 space-y-4">
                {[
                  "Submit your product request",
                  "We match Turkish manufacturers",
                  "Receive quotes in your workspace",
                ].map((text, i) => (
                  <li key={text} className="flex gap-3 items-start group/item">
                    <span className="w-7 h-7 rounded-full bg-[hsl(var(--brand-red))] text-white font-mono text-xs font-bold grid place-items-center shrink-0 transition-transform duration-300 group-hover/item:scale-110">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm text-[hsl(var(--brand-navy))]/80 leading-relaxed pt-0.5 group-hover/item:text-[hsl(var(--brand-navy))] transition-colors">
                      {text}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-neutral-200/80 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, label: "Verified manufacturers" },
            { icon: Zap, label: "30-second RFQ" },
            { icon: Globe2, label: "Global delivery" },
            { icon: Factory, label: "Direct from source" },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-default transition-all duration-300 hover:bg-[hsl(var(--brand-navy))]/[0.04] hover:-translate-y-0.5 group"
            >
              <div className="icon-tile w-9 h-9">
                <Icon className="w-4 h-4 text-[hsl(var(--brand-navy))] group-hover:text-[hsl(var(--brand-red))] transition-colors" strokeWidth={1.75} />
              </div>
              <span className="text-sm font-medium text-neutral-700">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="section-label">How it works</div>
            <h2 className="mt-4 text-3xl lg:text-4xl font-light tracking-tight text-[hsl(var(--brand-navy))]">
              Procurement, without the paperwork.
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed">
              Traditional RFQs take weeks. DeMaxtore compresses them into seconds — so you can focus
              on comparing quotes, not filling forms.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
            {[
              { num: "01", title: "Describe", body: "Tell us the product, quantity, and destination." },
              { num: "02", title: "Match", body: "We invite verified Turkish manufacturers to quote." },
              { num: "03", title: "Evaluate", body: "Compare quotations in your private workspace." },
            ].map((s) => (
              <div key={s.num} className="card-panel-hover p-8 group">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-red))]/10 grid place-items-center transition-colors group-hover:bg-[hsl(var(--brand-red))]/15">
                  <span className="text-[hsl(var(--brand-red))] font-mono text-sm font-bold">{s.num}</span>
                </div>
                <div className="mt-5 text-xl font-medium text-[hsl(var(--brand-navy))]">{s.title}</div>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[hsl(var(--brand-navy))] text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--brand-navy-dark))] via-transparent to-[hsl(var(--brand-navy-mid))]" />
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-[hsl(var(--brand-red))]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-8 items-center">
          <h2 className="text-3xl lg:text-4xl font-light tracking-tight">
            Ready to source from Türkiye?
          </h2>
          <div className="flex lg:justify-end">
            <Link to="/rfq/new">
              <Button size="lg" className="btn-cta h-14 px-8 group">
                Start Your RFQ{" "}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <BrandLogo to="/" variant="compact" />
          <span className="text-sm text-neutral-500">
            © {new Date().getFullYear()} DeMaxtore. All rights reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}
